import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CATEGORY_LABEL = {
  "cuidado-personal": "Cuidado Personal",
  "hogar-limpio": "Hogar Limpio",
  "nutricion-limpia": "Nutrición Limpia",
  "longevidad-celular": "Longevidad",
  general: "General",
};

const BlogListPage = () => {
  const [articles, setArticles] = useState([]);
  const [lines, setLines] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/taxonomy").then(({ data }) => setLines(data.lines));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/articles", { params: { category: filter || undefined } })
      .then(({ data }) => setArticles(data))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <section className="relative overflow-hidden aura border-b border-border">
        <div className="absolute inset-0 bio-grid opacity-30" />
        <div className="relative z-10 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14">
          <span className="font-mono-tech text-xs uppercase tracking-widest text-emerald-400">Guías & Educación</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl">
            Aprende a vivir sin toxinas
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">Artículos curados por línea para tomar mejores decisiones de salud y longevidad.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <button onClick={() => setFilter("")} data-testid="blog-filter-all"
              className={`rounded-full border px-4 py-2 text-sm transition-all ${!filter ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-border hover:border-emerald-500/50 text-muted-foreground"}`}>
              Todas
            </button>
            {lines.map((l) => (
              <button key={l.id} onClick={() => setFilter(l.id)} data-testid={`blog-filter-${l.id}`}
                className={`rounded-full border px-4 py-2 text-sm transition-all ${filter === l.id ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-border hover:border-emerald-500/50 text-muted-foreground"}`}>
                {l.short}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-12">
        {loading ? (
          <p className="text-center text-muted-foreground py-20 font-mono-tech text-sm animate-pulse">Cargando artículos…</p>
        ) : articles.length === 0 ? (
          <p data-testid="blog-empty" className="text-center text-muted-foreground py-20">No hay artículos en esta categoría todavía.</p>
        ) : (
          <>
            {articles.find((a) => a.featured) && (() => {
              const f = articles.find((a) => a.featured);
              return (
                <Link to={`/blog/${f.slug}`} data-testid="featured-article" className="group block mb-12 overflow-hidden rounded-3xl border border-emerald-500/30 bg-card hover:shadow-[0_8px_40px_rgba(16,185,129,0.15)] transition-all">
                  <div className="grid md:grid-cols-2">
                    <div className="relative aspect-[16/10] md:aspect-auto overflow-hidden bg-secondary">
                      <img src={f.cover_image} alt={f.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center">
                      <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider text-slate-950 font-bold">
                        <Star className="h-3 w-3" fill="currentColor" /> Guía destacada
                      </span>
                      <h2 className="mt-4 font-display text-2xl lg:text-3xl font-extrabold tracking-tight leading-tight group-hover:text-emerald-400 transition-colors">{f.title}</h2>
                      <p className="mt-3 text-muted-foreground leading-relaxed line-clamp-3">{f.excerpt}</p>
                      <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {f.read_minutes} min</span>
                        <span>·</span><span>{f.author}</span>
                      </div>
                      <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-emerald-400">Leer guía destacada <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.filter((a) => !a.featured).map((a, i) => (
              <motion.article key={a.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                data-testid={`article-card-${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card hover:border-emerald-500/50 hover:shadow-[0_8px_30px_rgba(16,185,129,0.12)] transition-all duration-300 hover:-translate-y-1">
                <Link to={`/blog/${a.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-secondary">
                  <img src={a.cover_image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 rounded-full bg-emerald-500/90 px-3 py-1 text-[10px] font-mono-tech uppercase tracking-wider text-slate-950">
                    {CATEGORY_LABEL[a.category] || a.category}
                  </span>
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {a.read_minutes} min</span>
                    <span>·</span>
                    <span>{a.author}</span>
                  </div>
                  <Link to={`/blog/${a.slug}`}>
                    <h2 className="font-display text-lg font-semibold leading-snug group-hover:text-emerald-400 transition-colors line-clamp-2">{a.title}</h2>
                  </Link>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                  <Link to={`/blog/${a.slug}`} className="mt-auto pt-4 inline-flex items-center gap-1 text-sm font-medium text-emerald-400 hover:gap-2 transition-all">
                    Leer artículo <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default BlogListPage;
